import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { isInvalidField } from 'KnmErknmPackage/MainModule/common/validateInvalidFields';
import { KnmInvalidFieldEnum } from 'KnmPackage/MainModule/enums/KnmInvalidFieldEnum';
import { IKnmErknmFormObject } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormObject';
import {
  getUserRoleIsProsecutorOrSuperProsecutor,
  getUserRoleIsSupervisor,
} from 'AuthPackage/MainModule/selectors/authSelectors';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IDictionaryDangerClassSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryDangerClassSelectOption';
import { IDictionarySubjectTypeSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySubjectTypeSelectOption';
import { IDictionarySubjectKindSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySubjectKindSelectOption';
import { IDictionaryRiskCategorySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryRiskCategorySelectOption';

type Props = {
  value: IKnmErknmFormObject;
  isWatchOnly: boolean;
  hazardClassApplies: boolean | undefined;
  isErknm: boolean;
  canRefreshControlObject?: boolean;
  created: string | null | undefined;
  dangerClassesSelectOptions: IDictionaryDangerClassSelectOption[] | undefined;
  objectKindsSelectOptions: IDictionarySubjectKindSelectOption[] | undefined;
  objectTypesSelectOptions?: IDictionarySubjectTypeSelectOption[] | undefined;
  riskCategoriesSelectOptions: IDictionaryRiskCategorySelectOption[] | undefined;
  invalidFields?: string[];
};

export const useObjectFieldsDisabled = ({
  value,
  isWatchOnly,
  hazardClassApplies,
  isErknm,
  created,
  invalidFields,
  dangerClassesSelectOptions,
  objectKindsSelectOptions,
  objectTypesSelectOptions,
  riskCategoriesSelectOptions,
  canRefreshControlObject,
}: Props) => {
  const { objectType, objectKind, objectSubKind, dangerClass, riskCategory } = value;
  const hasBackendId: boolean = !!value.ervkId || !!value.cosId;

  const isSupervisor = useSelector(getUserRoleIsSupervisor);
  const settings = useSelector(getAdminFetchSettings);
  const isProsecutorOrSuperProsecutor = useSelector(getUserRoleIsProsecutorOrSuperProsecutor);

  const isBlockRiskCategoryOrDangerClass = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_4_1_28_STARTS_FROM,
  );

  const isUseOnlyLowRiskInManualObject = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_223_4_1_42_STARTS_FROM,
  );

  const isObjectTypeInvalid = useMemo(
    () =>
      !!invalidFields && isInvalidField(KnmInvalidFieldEnum.OBJECTS_ERKNM_OBJECT_TYPE, invalidFields, value.guid || ''),
    [invalidFields, value],
  );

  const isObjectKindInvalid = useMemo(
    () =>
      !!invalidFields && isInvalidField(KnmInvalidFieldEnum.OBJECTS_ERKNM_OBJECT_KIND, invalidFields, value.guid || ''),
    [invalidFields, value],
  );

  const isObjectSubKindInvalid = useMemo(
    () =>
      !!invalidFields &&
      isInvalidField(KnmInvalidFieldEnum.OBJECTS_ERKNM_OBJECT_SUB_KIND, invalidFields, value.guid || ''),
    [invalidFields, value],
  );

  const isReadOnlyBase = hasBackendId || isWatchOnly || value.isFilledFromLink;

  const isCurrentObjectTypeAllowed = !objectType
    ? true
    : isObjectTypeInvalid ||
      objectTypesSelectOptions?.length === 0 ||
      !!objectTypesSelectOptions?.find(
        i => i.value.dictId === objectType.value.dictId && i.value.dictVersionId === objectType.value.dictVersionId,
      );

  const isCurrentObjectKindAllowed = !objectKind
    ? true
    : isObjectKindInvalid ||
      objectKindsSelectOptions?.length === 0 ||
      !!objectKindsSelectOptions?.find(
        i => i.value.dictId === objectKind.value.dictId && i.value.dictVersionId === objectKind.value.dictVersionId,
      );

  const isCurrentObjectSubKindAllowed = !objectSubKind
    ? true
    : isObjectSubKindInvalid ||
      objectKindsSelectOptions?.length === 0 ||
      !!objectKindsSelectOptions?.find(
        i =>
          i.value.dictId === objectSubKind.value.dictId && i.value.dictVersionId === objectSubKind.value.dictVersionId,
      );

  const isCurrentRiskCategoryAllowed = !riskCategory
    ? true
    : riskCategoriesSelectOptions?.length === 0 ||
      !!riskCategoriesSelectOptions?.find(
        i => i.value.dictId === riskCategory.value.dictId && i.value.dictVersionId === riskCategory.value.dictVersionId,
      );

  const isCurrentDangerClassAllowed = !dangerClass
    ? true
    : dangerClassesSelectOptions?.length === 0 ||
      !!dangerClassesSelectOptions?.find(
        i => i.value.dictId === dangerClass.value.dictId && i.value.dictVersionId === dangerClass.value.dictVersionId,
      );

  const isObjectTypeLocked = !isErknm ? isReadOnlyBase : isReadOnlyBase || !isCurrentObjectTypeAllowed;
  const isObjectKindLocked = !isErknm ? isReadOnlyBase : isReadOnlyBase || !isCurrentObjectKindAllowed;
  const isObjectSubKindLocked = !isErknm ? isReadOnlyBase : isReadOnlyBase || !isCurrentObjectSubKindAllowed;
  const isRiskCategoryLocked = !isErknm ? isReadOnlyBase : isReadOnlyBase || !isCurrentRiskCategoryAllowed;
  const isDangerClassLocked = !isErknm ? isReadOnlyBase : isReadOnlyBase || !isCurrentDangerClassAllowed;

  const isObjectKindAndSubKindLockedByObjectType = !objectType || isObjectTypeInvalid;

  const isDangerClassDisabled =
    (isDangerClassLocked && (!!value.ervkId || !dangerClass?.inspectionResult)) ||
    isProsecutorOrSuperProsecutor ||
    (isBlockRiskCategoryOrDangerClass && !hazardClassApplies);

  const isAddressDisabled =
    !isSupervisor || isWatchOnly || value.autocomplete || hasBackendId || value.isFilledFromLink;

  const isObjectSubKindDisabled =
    !isSupervisor ||
    (isObjectSubKindLocked && (!!value.ervkId || !objectSubKind?.inspectionResult)) ||
    isObjectKindAndSubKindLockedByObjectType;

  const isObjectTypeDisabled =
    !isSupervisor || (isObjectTypeLocked && (!!value.ervkId || !objectType?.inspectionResult));

  const isObjectKindDisabled =
    !isSupervisor ||
    (isObjectKindLocked && (!!value.ervkId || !objectKind?.inspectionResult)) ||
    isObjectKindAndSubKindLockedByObjectType;

  const isRiskCategoryDisabled =
    (isUseOnlyLowRiskInManualObject && !value.ervkId) ||
    (isRiskCategoryLocked && (!!value.ervkId || !riskCategory?.inspectionResult)) ||
    isProsecutorOrSuperProsecutor ||
    (isBlockRiskCategoryOrDangerClass && hazardClassApplies);

  const isRefreshButtonDisabled = !value.cosId || !canRefreshControlObject;

  return {
    isRiskCategoryDisabled,
    isObjectKindDisabled,
    isRefreshButtonDisabled,
    isObjectTypeDisabled,
    isObjectSubKindDisabled,
    isAddressDisabled,
    isDangerClassDisabled,
  };
};
